import { useState } from 'react';
import { ApiError, COOKIE_MODE, api, isServerConfigError, serverConfigMessage, setAdminPassword, type ApiSettings } from '../api';
import { t } from '../i18n';
import { KkpLogo } from './KkpLogo';
import type { Session } from '../session';

/**
 * The gate in front of the whole control panel.
 *
 * Cookie mode  — username + password to `POST /auth/login`. The BFF mints the session; the
 *                browser is handed an HttpOnly cookie it cannot read and the readable CSRF
 *                companion. **The password is never stored, anywhere**: it lives in this
 *                component's state for the length of one submit and is cleared on success.
 * Password mode — the old behaviour, unchanged: one shared admin password, kept in
 *                sessionStorage for this tab and sent as `x-admin-password`.
 *
 * Either way the SERVER decides. There is no client-side check to bypass.
 */


export function LoginScreen({
  settings,
  message,
  onAuthenticated,
}: {
  settings: ApiSettings;
  /** Why the login screen is showing — e.g. after a 401 mid-session. */
  message?: string | null;
  /** Cookie mode hands the fresh session up; password mode has none to hand. */
  onAuthenticated: (session?: Session) => void;
}) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  /** A 502/503 from the BFF: nothing the person typing can fix. Shown apart from `error`. */
  const [configError, setConfigError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const incomplete = COOKIE_MODE ? username.trim().length === 0 || password.length === 0 : password.length === 0;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (incomplete || busy) return;
    setBusy(true);
    setError(null);
    setConfigError(null);

    if (COOKIE_MODE) {
      try {
        const session = await api.login(settings, username.trim(), password);
        // Out of memory the moment it has been exchanged for a session.
        setPassword('');
        onAuthenticated(session);
      } catch (err) {
        if (isServerConfigError(err)) setConfigError(serverConfigMessage(err));
        // QA-A-08: only the RIGHT password on a locked account hears that it is locked.
        else if (err instanceof ApiError && err.status === 401 && err.code === 'account_locked') setError(t('login.locked.message', { n: err.retryAfterMinutes ?? 15 }));
        else if (err instanceof ApiError && err.status === 401) setError(t('login.bad-credentials.message'));
        else setError((err as Error).message);
      } finally {
        setBusy(false);
      }
      return;
    }

    // Store first: the client sends the header from here, so the probe below is the
    // real check. It is cleared again if the server says no.
    setAdminPassword(password);
    try {
      await api.getRules(settings);
      setPassword('');
      onAuthenticated();
    } catch (err) {
      // api.request() has already cleared the stored password on a 401.
      setError(err instanceof ApiError && err.status === 401 ? t('login.bad-password.message') : (err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login-screen">
      {/* The same purple band the console proper wears, so signing in does not look like a
          different product. The mark and the product name, and then the two fields. */}
      <header className="masthead">
        <KkpLogo />
        <span className="masthead-title">{t('shell.brand.name')}</span>
      </header>
      <div className="login-body">
        <form className="login-card" onSubmit={submit} aria-label={t('login.signin.action')}>
          <h1>{t('login.title.heading')}</h1>

          {COOKIE_MODE && (
            <label className="field">
              {t('login.username.label')}
              <input
                type="text"
                aria-label={t('login.username.a11y')}
                autoFocus
                autoComplete="username"
                value={username}
                onChange={(ev) => setUsername(ev.target.value)}
              />
            </label>
          )}

          <label className="field">
            {COOKIE_MODE ? t('login.password.label') : t('login.admin-password.label')}
            <input
              type="password"
              aria-label={COOKIE_MODE ? t('login.password.a11y') : t('login.admin-password.a11y')}
              autoFocus={!COOKIE_MODE}
              autoComplete="current-password"
              value={password}
              onChange={(ev) => setPassword(ev.target.value)}
            />
          </label>

          {message && !error && (
            <div className="banner warn small" role="status" data-testid="login-message">
              {message}
            </div>
          )}

          {configError && (
            <div className="banner error" role="alert" data-testid="login-config-error">
              {configError}
            </div>
          )}

          {error && (
            <div className="banner error" role="alert" data-testid="login-error">
              {error}
            </div>
          )}

          <button type="submit" className="btn primary" disabled={busy || incomplete}>
            {busy ? t('btn.checking.label') : t('login.signin.action')}
          </button>
        </form>
      </div>
    </div>
  );
}
